import { useEffect, useState } from 'react';
import { createMMKV } from 'react-native-mmkv';

export const storage = createMMKV();

export function getItem<T>(key: string): T | null {
  const value = storage.getString(key);
  if (value === undefined) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

export async function setItem<T>(key: string, value: T) {
  storage.set(key, JSON.stringify(value));
}

export async function removeItem(key: string) {
  storage.remove(key);
}

export async function clearAll() {
  storage.clearAll();
}

export function clearByPrefix(prefix: string) {
  for (const key of storage.getAllKeys()) {
    if (key.startsWith(prefix)) {
      storage.remove(key);
    }
  }
}

// `useState`-compatible hook that mirrors its value to MMKV under `key`.
// Initialises from the stored value (falling back to `fallback`) and writes
// back on every change. `key` is expected to be stable for the lifetime of
// the hook.
export function usePersistedState<T>(key: string, fallback: T) {
  const [state, setState] = useState<T>(() => {
    const stored = getItem<T>(key);
    return stored != null ? stored : fallback;
  });

  useEffect(() => {
    if (state === undefined) {
      storage.remove(key);
      return;
    }
    storage.set(key, JSON.stringify(state));
  }, [key, state]);

  return [state, setState] as const;
}
